import { getHsApiClientIamRoleArn, getHSApiConfig, getServiceRegion } from '../constants/config';
import { fetchTemporaryAwsCredentials } from './auth';
import basicCallWrapper from './clientCall';

import { MidwayIdentityCredentialSTS } from '@amzn/midway-identity-credential-provider';

// This client and config is auto-generated from AWSJavaScriptSDK-C2J-Build using Coral model and exposed by our TS client
import { HammerstoneExposedRESTfulServiceLambda as HsApi } from '@amzn/aws-hammerstone-exposed-restful-service-typescript-client';
import { AWSError, Request } from 'aws-sdk/global';

let apiClient: HsApi;

export const getHammerstoneApiClient = async (): Promise<HsApi> => {
	const config = getHSApiConfig();

	// Not every region has an HSAPI configuration (e.g. regions where RIP lacks the react-ui
	// account, such as ZHY today). Fail explicitly with a clear message rather than throwing an
	// opaque TypeError when the config below is dereferenced.
	if (!config) {
		throw new Error(
			`Hammerstone API is not available in region '${getServiceRegion()}': ` +
				'no HSAPI configuration is published for this region. ' +
				'HSAPI-dependent features are unavailable here.'
		);
	}

	// get/refresh the temporary SigV4 credentials
	const iamRoleArn = getHsApiClientIamRoleArn(config.iamRolePartition, config.iamRoleAccount);
	const awsCredentials: MidwayIdentityCredentialSTS = await fetchTemporaryAwsCredentials(iamRoleArn);

	// Renew the client if its credentials expired and were renewed
	if (!apiClient || clientNeedsRenewal(apiClient, awsCredentials)) {
		const hsApiClientConfig: HsApi.ClientConfiguration = {
			region: config.region,
			endpoint: config.endpoint,
			credentials: {
				accessKeyId: awsCredentials.AccessKeyId,
				secretAccessKey: awsCredentials.SecretAccessKey,
				sessionToken: awsCredentials.SessionToken,
			},

			// We can also set optional params found in ClientConfiguration (subclass of ServiceConfigurationOptions / ConfigurationOptions in AWS SDK)
			// Your IDE should be able to find these parameter types by checking the AWS SDK source code after Cmd-clicking on the ClientConfiguration type above.

			// As of 02/14/2023, AWS is on Signature version 4: https://docs.aws.amazon.com/general/latest/gr/signing-aws-api-requests.html
			// Please upgrade this if and when AWS changes the general signature version.
			signatureVersion: 'v4',

			// customize exponential backoff
			// 3 retries is standard for fail-fast, but it can cause issues for P99 cold start cases if the cold start call itself fails
			// Since 3 retries don't occur within the 4-second exponential retry window while cold starts take up to 10 seconds, there will be no further retries
			// TODO: optimize lambda cold start lambdas further. at time of creation, we were limited as by the BONES Coral runtime. Migrate to CoralLambdaEndpoint and keep optimizing!
			maxRetries: 3,
			retryDelayOptions: {
				// milliseconds to wait before an API retry is initiated, base in exponential backoff
				// AWS default is 100ms, but average HS API calls are 100ms to 1,000ms.
				// This caused APIs to be called multiple times for every page, since a previous request would still be open
				// Cold starts can present issues as they are typically 5-10 seconds long.
				base: 1000,
			},
		};
		apiClient = new HsApi(hsApiClientConfig);
	}
	return apiClient;
};

const clientNeedsRenewal = (apiClient: HsApi, newCredentials: MidwayIdentityCredentialSTS): boolean => {
	const currentCreds = apiClient.config.credentials;
	return currentCreds?.sessionToken !== newCredentials.SessionToken;
};

function createHammerstoneAPIFunction<RequestType, ResponseType>(apiName: keyof HsApi) {
	return async function (input: RequestType) {
		const apiClient = await getHammerstoneApiClient();
		return basicCallWrapper<RequestType, ResponseType>(
			apiClient,
			apiClient[apiName] as (input: RequestType) => Request<ResponseType, AWSError>
		)(input);
	};
}

export const DeleteActivityInfo = createHammerstoneAPIFunction<
	HsApi.DeleteActivityInfoRequest,
	HsApi.DeleteActivityInfoResponse
>('deleteActivityInfo');

export const DeletePipelineInfo = createHammerstoneAPIFunction<
	HsApi.DeletePipelineInfoRequest,
	HsApi.DeletePipelineInfoResponse
>('deletePipelineInfo');

export const GetActivityInfo = createHammerstoneAPIFunction<
	HsApi.GetActivityInfoRequest,
	HsApi.GetActivityInfoResponse
>('getActivityInfo');

export const GetInstancesByActivity = createHammerstoneAPIFunction<
	HsApi.GetInstanceInfoByActivityIdRequest,
	HsApi.GetInstanceInfoByActivityIdResponse
>('getInstanceInfoByActivityId');

export const GetInstanceById = createHammerstoneAPIFunction<
	HsApi.GetInstanceInfoByInstanceIdRequest,
	HsApi.GetInstanceInfoByInstanceIdResponse
>('getInstanceInfoByInstanceId');

export const GetInstanceInfoByPipelineName = createHammerstoneAPIFunction<
	HsApi.GetInstanceInfoByPipelineNameRequest,
	HsApi.GetInstanceInfoByPipelineNameResponse
>('getInstanceInfoByPipelineName');

export const GetGroupPipelineNamesInfo = createHammerstoneAPIFunction<
	HsApi.GetGroupPipelineNamesInfoRequest,
	HsApi.GetGroupPipelineNamesInfoResponse
>('getGroupPipelineNamesInfo');

export const GetGroupsUsingLdap = createHammerstoneAPIFunction<
	HsApi.GetGroupsUsingLdapRequest,
	HsApi.GetGroupsUsingLdapResponse
>('getGroupsUsingLdap');

export const GetUserLdapGroups = createHammerstoneAPIFunction<
	HsApi.GetUserLdapGroupsRequest,
	HsApi.GetUserLdapGroupsResponse
>('getUserLdapGroups');

export const GetPipelineInfo = createHammerstoneAPIFunction<
	HsApi.GetPipelineInfoRequest,
	HsApi.GetPipelineInfoResponse
>('getPipelineInfo');

export const PutPipelineInfo = createHammerstoneAPIFunction<
	HsApi.PutPipelineInfoRequest,
	HsApi.PutPipelineInfoResponse
>('putPipelineInfo');

export const PutActivityInfo = createHammerstoneAPIFunction<
	HsApi.PutActivityInfoRequest,
	HsApi.PutActivityInfoResponse
>('putActivityInfo');

export const PutInstanceStatus = createHammerstoneAPIFunction<
	HsApi.PutInstanceStatusRequest,
	HsApi.PutInstanceStatusResponse
>('putInstanceStatus');

export const PostPipelineInfo = createHammerstoneAPIFunction<
	HsApi.PostPipelineInfoRequest,
	HsApi.PostPipelineInfoResponse
>('postPipelineInfo');

export const CreateActivityInfo = createHammerstoneAPIFunction<
	HsApi.CreateActivityInfoRequest,
	HsApi.CreateActivityInfoResponse
>('createActivityInfo');

// Tickety APIs thru Hammerstone API
export const GetCategories = createHammerstoneAPIFunction<HsApi.GetCategoriesRequest, HsApi.TicketyListCtiResponse>(
	'getCategories'
);
export const GetTypes = createHammerstoneAPIFunction<HsApi.GetTypesRequest, HsApi.TicketyListCtiResponse>('getTypes');
export const GetItems = createHammerstoneAPIFunction<HsApi.GetItemsRequest, HsApi.TicketyListCtiResponse>('getItems');

// Ad Hoc Query APIs
export const GetAdHocQueries = createHammerstoneAPIFunction<
	HsApi.GetAdHocQueriesRequest,
	HsApi.GetAdHocQueriesResponse
>('getAdHocQueries');

export const GetAdHocQuery = createHammerstoneAPIFunction<HsApi.GetAdHocQueryRequest, HsApi.GetAdHocQueryResponse>(
	'getAdHocQuery'
);

export const CreateAdHocQuery = createHammerstoneAPIFunction<
	HsApi.CreateAdHocQueryRequest,
	HsApi.CreateAdHocQueryResponse
>('createAdHocQuery');

export const PutAdHocQuery = createHammerstoneAPIFunction<HsApi.PutAdHocQueryRequest, HsApi.PutAdHocQueryResponse>(
	'putAdHocQuery'
);
export const GetAdHocQueryOutput = createHammerstoneAPIFunction<
	HsApi.GetAdHocQueryOutputRequest,
	HsApi.GetAdHocQueryOutputResponse
>('getAdHocQueryOutput');

export const GetDownstreamActivityPreconditions = createHammerstoneAPIFunction<
	HsApi.GetDownstreamActivityPreconditionsRequest,
	HsApi.GetDownstreamActivityPreconditionsResponse
>('getDownstreamActivityPreconditions');

export const GetClustersByGroupInfo = createHammerstoneAPIFunction<
	HsApi.GetClustersByGroupInfoRequest,
	HsApi.GetClustersByGroupInfoResponse
>('getClustersByGroupInfo');

export const SearchActivity = createHammerstoneAPIFunction<HsApi.SearchActivityRequest, HsApi.SearchActivityResponse>(
	'searchActivity'
);

export const GetActivitySchedulerInfo = createHammerstoneAPIFunction<
	HsApi.GetActivitySchedulerInfoRequest,
	HsApi.GetActivitySchedulerInfoResponse
>('getActivitySchedulerInfo');

export const GetActivityRevisions = createHammerstoneAPIFunction<
	HsApi.GetActivityRevisionsRequest,
	HsApi.GetActivityRevisionsResponse
>('getActivityRevisions');

export const GetActivityRevisionInfo = createHammerstoneAPIFunction<
	HsApi.GetActivityRevisionInfoRequest,
	HsApi.GetActivityRevisionInfoResponse
>('getActivityRevisionInfo');

export const DeleteActivityRevisionInfo = createHammerstoneAPIFunction<
	HsApi.DeleteActivityRevisionInfoRequest,
	HsApi.DeleteActivityRevisionInfoResponse
>('deleteActivityRevisionInfo');

export const GetDataAccessIamRoles = createHammerstoneAPIFunction<
	HsApi.GetDataAccessIamRolesRequest,
	HsApi.GetDataAccessIamRolesResponse
>('getDataAccessIamRoles');

export const GetDataAccessOdins = createHammerstoneAPIFunction<
	HsApi.GetDataAccessOdinsRequest,
	HsApi.GetDataAccessOdinsResponse
>('getDataAccessOdins');
